import { useEffect, useRef, useMemo, useState } from 'react';
import * as PIXI from 'pixi.js';
import type { ScoreTimeline, PracticeConfig, Note, Measure } from '@/types';
import { diffPool, notePoolEntryStale } from '@/utils/waterfallPool';
import { filterNotesByHand, noteColorState } from '@/utils/waterfallNoteState';
import { usePlaybackStore } from '@/stores';

interface WaterfallDisplayProps {
  timeline: ScoreTimeline;
  width: number;
  height: number;
  config: PracticeConfig;
  activeNotes: Set<number>;
}

// Piano key layout constants
const TOTAL_KEYS = 88;
const FIRST_MIDI_NOTE = 21; // A0
const WHITE_KEYS = [0, 2, 4, 5, 7, 9, 11];
const PIXELS_PER_SECOND = 200;

type KeyPosition = { x: number; width: number; isBlack: boolean };

interface NotePoolEntry {
  node: PIXI.Container; // positioned per frame via node.y only
  bar: PIXI.Graphics; // child at local (barX, 0); geometry redrawn only when stale (see notePoolEntryStale)
  label: PIXI.Text | null; // child iff noteHeight > 22; recreated (not mutated) alongside a stale-triggered bar redraw
  lastColor: number;
  lastAlpha: number;
  lastPitch: number;
  lastDuration: number;
  note: Note;
}

interface MeasurePoolEntry {
  node: PIXI.Container; // positioned per frame via node.y only; children never change
}

/**
 * OnlinePianist-style waterfall visualization using PixiJS.
 * Notes fall from top and land directly onto the keyboard at the bottom edge.
 * Left hand (staff 2) = green, right hand (staff 1) = blue -- see
 * utils/waterfallNoteState.ts, never derived from pitch.
 *
 * OTO-21: notes and measure bar-lines/labels are pooled and reused across
 * frames (keyed by Note.id / Measure.number) instead of destroyed and
 * rebuilt every frame. The prior per-frame teardown allocated/rasterized
 * PIXI.Graphics/Text at 60fps, stalling the main thread enough to delay the
 * notation cursor's scroll write (visible "pause, then jump"). Steady-state
 * frames now only set `node.y`; bar geometry is redrawn solely on a
 * color/alpha transition (note enters/exits "sounding"), and PIXI.Text is
 * never mutated after creation (re-rasterizes its texture otherwise).
 */
export function WaterfallDisplay({
  timeline,
  width,
  height,
  config,
  activeNotes,
}: WaterfallDisplayProps) {
  // OTO-21: subscribed internally (was a prop from PracticeView) -- this is
  // the one component meant to re-render every frame during playback; the
  // granularity choice here is "as fine as possible", the opposite of the
  // quantized/low-frequency selectors used elsewhere in the practice view.
  const currentTime = usePlaybackStore((s) => s.position.currentTime);
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const notesContainerRef = useRef<PIXI.Container | null>(null);
  const notesLayerRef = useRef<PIXI.Container | null>(null);
  const measuresLayerRef = useRef<PIXI.Container | null>(null);
  const notePoolRef = useRef<Map<string, NotePoolEntry>>(new Map());
  const measurePoolRef = useRef<Map<number, MeasurePoolEntry>>(new Map());

  // Visual config
  const lookAheadTime = height / PIXELS_PER_SECOND;

  // OTO-21 Gate B fix: `app.init()` is async and yields to the event loop;
  // the render-pass effect below can run (and bail on still-null refs)
  // before it resolves. Bumping this once init actually finishes forces
  // that effect to re-run so a paused initial load (or paused resize) still
  // renders notes instead of staying blank. Mirrors NotationDisplay's
  // `renderVersion` pattern.
  const [rendererReady, setRendererReady] = useState(0);

  // Calculate key positions matching the keyboard layout below
  const keyPositions = useMemo(() => {
    const positions: Map<number, KeyPosition> = new Map();

    let whiteKeyCount = 0;
    for (let i = 0; i < TOTAL_KEYS; i++) {
      const midiNote = FIRST_MIDI_NOTE + i;
      if (WHITE_KEYS.includes(midiNote % 12)) whiteKeyCount++;
    }

    const whiteKeyWidth = width / whiteKeyCount;
    const blackKeyWidth = whiteKeyWidth * 0.6;

    let whiteKeyIndex = 0;
    for (let i = 0; i < TOTAL_KEYS; i++) {
      const midiNote = FIRST_MIDI_NOTE + i;
      const noteInOctave = midiNote % 12;
      const isWhiteKey = WHITE_KEYS.includes(noteInOctave);

      if (isWhiteKey) {
        positions.set(midiNote, {
          x: whiteKeyIndex * whiteKeyWidth,
          width: whiteKeyWidth - 1,
          isBlack: false,
        });
        whiteKeyIndex++;
      } else {
        const prevWhiteKeyX = (whiteKeyIndex - 1) * whiteKeyWidth;
        positions.set(midiNote, {
          x: prevWhiteKeyX + whiteKeyWidth - blackKeyWidth / 2,
          width: blackKeyWidth,
          isBlack: true,
        });
      }
    }

    return positions;
  }, [width]);

  // Initialize PixiJS
  useEffect(() => {
    if (!containerRef.current || width === 0 || height === 0) return;

    // Guards against React 18 StrictMode's mount->cleanup->mount double-invoke racing
    // PIXI.Application's async init(): without it, cleanup can call destroy() on an
    // app whose init() hasn't resolved yet, or destroy() can run twice on the same
    // instance (PixiJS's own ResizePlugin nulls _cancelResize after the first destroy,
    // so a second call throws `this._cancelResize is not a function`) -- uncaught, that
    // propagates out of the effect cleanup and (no error boundary in this app) unmounts
    // the entire React tree. Mirrors the same fix applied to NotationDisplay.tsx.
    let cancelled = false;
    const app = new PIXI.Application();
    const notePool = notePoolRef.current;
    const measurePool = measurePoolRef.current;

    const destroyApp = () => {
      try {
        app.destroy(true, { children: true });
      } catch (err) {
        // OTO-21 fix: do NOT rethrow. This runs on every unmount *and*
        // every [width, height] resize (not just teardown), with no
        // ErrorBoundary anywhere in frontend/src -- an uncaught error here
        // propagates out of the effect cleanup and unmounts the entire
        // React tree. Real failure modes (WebGL context loss from tab
        // backgrounding, GPU driver reset, low memory) must degrade to a
        // logged error, not a full-app crash on a hot path.
        console.error(
          '[WaterfallDisplay/destroy] Failed: pixi app.destroy() | reason=',
          err instanceof Error ? err.message : err
        );
      }
    };

    const initApp = async () => {
      await app.init({
        width,
        height,
        backgroundColor: 0x111111,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      if (cancelled) {
        destroyApp();
        return;
      }

      if (containerRef.current && app.canvas) {
        containerRef.current.appendChild(app.canvas as HTMLCanvasElement);
      }

      const notesContainer = new PIXI.Container();
      app.stage.addChild(notesContainer);

      // Z-order (back to front): notes, then measure lines/labels, then the
      // now-line — matches the original per-frame draw order, made explicit
      // here since all three now persist instead of being redrawn last.
      const notesLayer = new PIXI.Container();
      const measuresLayer = new PIXI.Container();
      notesContainer.addChild(notesLayer);
      notesContainer.addChild(measuresLayer);

      const nowLine = new PIXI.Graphics();
      nowLine.rect(0, height - 2, width, 2);
      nowLine.fill({ color: 0xffffff, alpha: 0.2 });
      notesContainer.addChild(nowLine);

      notesContainerRef.current = notesContainer;
      notesLayerRef.current = notesLayer;
      measuresLayerRef.current = measuresLayer;
      appRef.current = app;
      setRendererReady((v) => v + 1);
    };

    initApp().catch((err) => {
      console.error(
        '[WaterfallDisplay/init] Failed: pixi app.init() | reason=',
        err instanceof Error ? err.message : err
      );
      throw err;
    });

    return () => {
      cancelled = true;
      if (appRef.current === app) {
        destroyApp();
        appRef.current = null;
        notesContainerRef.current = null;
        notesLayerRef.current = null;
        measuresLayerRef.current = null;
        // Pooled nodes were destroyed by app.destroy(true, { children: true })
        // above; drop the now-stale references so a future mount starts clean.
        notePool.clear();
        measurePool.clear();
      }
    };
  }, [width, height]);

  useEffect(() => {
    const notesLayer = notesLayerRef.current;
    const measuresLayer = measuresLayerRef.current;
    if (!notesLayer || !measuresLayer || !appRef.current) return;

    const notePool = notePoolRef.current;
    const measurePool = measurePoolRef.current;

    // Filter by hand selection
    const notesToDisplay = filterNotesByHand(timeline.notes, config.hand);

    // Visible notes: currently sounding or upcoming within look-ahead
    const visibleNotes = notesToDisplay.filter((note) => {
      const noteEndTime = note.startTime + note.duration;
      return noteEndTime >= currentTime && note.startTime <= currentTime + lookAheadTime;
    });
    const visibleNotesByKey = new Map(visibleNotes.map((note) => [note.id, note]));
    const noteDiff = diffPool(new Set(notePool.keys()), new Set(visibleNotesByKey.keys()));

    for (const key of noteDiff.toRemove) {
      const entry = notePool.get(key);
      if (!entry) continue;
      notesLayer.removeChild(entry.node);
      entry.node.destroy({ children: true });
      notePool.delete(key);
    }

    for (const key of noteDiff.toAdd) {
      const note = visibleNotesByKey.get(key);
      const keyPos = note && keyPositions.get(note.pitch);
      if (!note || !keyPos) continue;
      const entry = createNoteEntry(note, keyPos, currentTime, height, activeNotes);
      notesLayer.addChild(entry.node);
      notePool.set(key, entry);
    }

    for (const key of noteDiff.toUpdate) {
      const note = visibleNotesByKey.get(key);
      const entry = notePool.get(key);
      const keyPos = note && keyPositions.get(note.pitch);
      if (!note || !entry || !keyPos) continue;
      updateNoteEntry(entry, note, keyPos, currentTime, height, activeNotes);
    }

    // Visible measures (same [-0.5, lookAheadTime] window as before)
    const visibleMeasures = timeline.measures.filter((measure) => {
      const timeUntil = measure.startTime - currentTime;
      return timeUntil >= -0.5 && timeUntil <= lookAheadTime;
    });
    const visibleMeasuresByKey = new Map(visibleMeasures.map((m) => [m.number, m]));
    const measureDiff = diffPool(new Set(measurePool.keys()), new Set(visibleMeasuresByKey.keys()));

    for (const key of measureDiff.toRemove) {
      const entry = measurePool.get(key);
      if (!entry) continue;
      measuresLayer.removeChild(entry.node);
      entry.node.destroy({ children: true });
      measurePool.delete(key);
    }

    for (const key of measureDiff.toAdd) {
      const measure = visibleMeasuresByKey.get(key);
      if (!measure) continue;
      const entry = createMeasureEntry(measure, currentTime, height, width);
      measuresLayer.addChild(entry.node);
      measurePool.set(key, entry);
    }

    for (const key of measureDiff.toUpdate) {
      const measure = visibleMeasuresByKey.get(key);
      const entry = measurePool.get(key);
      if (!measure || !entry) continue;
      entry.node.y = measureLineY(measure, currentTime, height);
    }
  }, [
    timeline,
    currentTime,
    config,
    activeNotes,
    keyPositions,
    height,
    width,
    lookAheadTime,
    rendererReady,
  ]);

  return <div ref={containerRef} className="w-full h-full" style={{ width, height }} />;
}

function noteBarGeometry(note: Note, keyPos: KeyPosition) {
  const noteHeight = Math.max(note.duration * PIXELS_PER_SECOND, 6);
  return {
    noteHeight,
    barWidth: keyPos.width - 2,
    barX: keyPos.x + 1,
    radius: Math.min(4, noteHeight / 2),
  };
}

function noteTopY(note: Note, currentTime: number, height: number): number {
  const timeUntilNote = note.startTime - currentTime;
  const noteHeight = Math.max(note.duration * PIXELS_PER_SECOND, 6);
  return height - timeUntilNote * PIXELS_PER_SECOND - noteHeight;
}

function drawNoteBar(
  bar: PIXI.Graphics,
  barX: number,
  barWidth: number,
  noteHeight: number,
  radius: number,
  color: number,
  alpha: number
): void {
  bar.roundRect(barX, 0, barWidth, noteHeight, radius);
  bar.fill({ color, alpha });
  bar.roundRect(barX, 0, barWidth, noteHeight, radius);
  bar.stroke({ color: 0xffffff, alpha: 0.15, width: 1 });
}

function createNoteLabel(
  pitch: number,
  barX: number,
  barWidth: number,
  noteHeight: number
): PIXI.Text {
  const label = new PIXI.Text({
    text: midiToNoteName(pitch),
    style: {
      fontSize: 11,
      fill: 0xffffff,
      fontFamily: 'system-ui, sans-serif',
      fontWeight: 'bold',
    },
  });
  label.x = barX + barWidth / 2 - label.width / 2;
  label.y = Math.max(3, noteHeight / 2 - label.height / 2);
  label.alpha = 0.9;
  return label;
}

function createNoteEntry(
  note: Note,
  keyPos: KeyPosition,
  currentTime: number,
  height: number,
  activeNotes: Set<number>
): NotePoolEntry {
  const { noteHeight, barWidth, barX, radius } = noteBarGeometry(note, keyPos);
  const { color, alpha } = noteColorState(note, currentTime, activeNotes);

  const bar = new PIXI.Graphics();
  drawNoteBar(bar, barX, barWidth, noteHeight, radius, color, alpha);

  const node = new PIXI.Container();
  node.addChild(bar);

  let label: PIXI.Text | null = null;
  if (noteHeight > 22) {
    label = createNoteLabel(note.pitch, barX, barWidth, noteHeight);
    node.addChild(label);
  }

  node.y = noteTopY(note, currentTime, height);

  return {
    node,
    bar,
    label,
    lastColor: color,
    lastAlpha: alpha,
    lastPitch: note.pitch,
    lastDuration: note.duration,
    note,
  };
}

function updateNoteEntry(
  entry: NotePoolEntry,
  note: Note,
  keyPos: KeyPosition,
  currentTime: number,
  height: number,
  activeNotes: Set<number>
): void {
  entry.node.y = noteTopY(note, currentTime, height);
  entry.note = note;

  const { color, alpha } = noteColorState(note, currentTime, activeNotes);
  const stale = notePoolEntryStale(
    {
      pitch: entry.lastPitch,
      duration: entry.lastDuration,
      color: entry.lastColor,
      alpha: entry.lastAlpha,
    },
    { pitch: note.pitch, duration: note.duration, color, alpha }
  );
  if (!stale) return;

  const { noteHeight, barWidth, barX, radius } = noteBarGeometry(note, keyPos);
  entry.bar.clear();
  drawNoteBar(entry.bar, barX, barWidth, noteHeight, radius, color, alpha);

  // A pool entry keyed by note id can outlive the underlying note's own
  // pitch/duration if upstream timeline data changes under the same id
  // (rare, unlike the per-frame color/alpha path above) -- the label text
  // encodes pitch, so it must be rebuilt too, not left stale.
  if (entry.label) {
    entry.node.removeChild(entry.label);
    entry.label.destroy();
    entry.label = null;
  }
  if (noteHeight > 22) {
    entry.label = createNoteLabel(note.pitch, barX, barWidth, noteHeight);
    entry.node.addChild(entry.label);
  }

  entry.lastColor = color;
  entry.lastAlpha = alpha;
  entry.lastPitch = note.pitch;
  entry.lastDuration = note.duration;
}

function measureLineY(measure: Measure, currentTime: number, height: number): number {
  return height - (measure.startTime - currentTime) * PIXELS_PER_SECOND;
}

function createMeasureEntry(
  measure: Measure,
  currentTime: number,
  height: number,
  width: number
): MeasurePoolEntry {
  const line = new PIXI.Graphics();
  line.rect(0, 0, width, 1);
  line.fill({ color: 0xffffff, alpha: 0.12 });

  const label = new PIXI.Text({
    text: `${measure.number}`,
    style: { fontSize: 10, fill: 0xffffff, fontFamily: 'system-ui, sans-serif' },
  });
  label.x = 6;
  label.y = -(label.height + 2);
  label.alpha = 0.3;

  const node = new PIXI.Container();
  node.addChild(line);
  node.addChild(label);
  node.y = measureLineY(measure, currentTime, height);

  return { node };
}

function midiToNoteName(midi: number): string {
  const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(midi / 12) - 1;
  return `${noteNames[midi % 12]}${octave}`;
}
